"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { Search, BookOpen, Calendar, UserCheck, ArrowRight, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { FormProvider } from "@/integrations/forms/FormProvider";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function ResultsConsumer({
  searchInput,
  onSearchSubmit
}: {
  searchInput: string;
  onSearchSubmit: (q: string) => void;
}) {
  const {
    posts,
    loading,
    error,
    refetch,
    total,
    currentPage,
    totalPages,
    nextPage,
    prevPage,
    isRefetching
  } = usePosts();
  const [localQuery, setLocalQuery] = useState(searchInput);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearchSubmit(localQuery);
  };
  return <div className="w-full">
      {/* Search Bar & Header */}
      <div className="mb-10 rounded-2xl border border-border bg-card p-6 md:p-8 shadow-sm">
        <FormProvider>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4 sm:flex-row items-center">
            <div className="relative w-full flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 size-5 text-muted-foreground" />
              <Input type="text" value={localQuery} onChange={e => setLocalQuery(e.target.value)} placeholder="Search symptoms, diets, breeds, or clinical advice..." className="h-12 w-full rounded-xl border-border bg-background pl-12 pr-4 text-base font-default text-foreground placeholder:text-muted-foreground focus-visible:ring-ring focus-visible:ring-2" />
            </div>
            <Button type="submit" className="h-12 w-full sm:w-auto px-7 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
              Search Library
            </Button>
          </form>
        </FormProvider>

        {/* Query status and count indicator */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-border/70 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <span>Showing results for:</span>
            <span className="font-semibold text-foreground">
              {searchInput ? `"${searchInput}"` : "All Clinical Topics"}
            </span>
            {total !== undefined && <span className="ml-1 inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-semibold text-foreground">
                {total} {total === 1 ? "article" : "articles"}
              </span>}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Sparkles className="size-3.5 text-muted-foreground" />
            <span>Reviewed against current veterinary standards</span>
          </div>
        </div>
      </div>

      {/* Loading state */}
      {loading && <div className="flex flex-col gap-6">
          {[0, 1, 2, 3].map((_, i) => <div key={i} data-index={i} className="flex flex-col md:flex-row gap-6 rounded-2xl border border-border bg-card p-6 animate-pulse">
              <div className="h-44 md:h-auto md:w-64 rounded-xl bg-muted shrink-0" />
              <div className="flex-1 space-y-4 py-2">
                <div className="h-5 w-24 rounded-full bg-muted" />
                <div className="h-7 w-3/4 rounded bg-muted" />
                <div className="h-4 w-full rounded bg-muted" />
                <div className="h-4 w-2/3 rounded bg-muted" />
                <div className="h-4 w-40 rounded bg-muted pt-2" />
              </div>
            </div>)}
        </div>}

      {/* Error state */}
      {error && !loading && <div className="rounded-2xl border border-destructive/20 bg-destructive/10 p-8 text-center text-foreground">
          <p className="text-base font-medium">Unable to load search results.</p>
          <Button onClick={() => refetch()} variant="outline" className="mt-4 border-border bg-background">
            Try Again
          </Button>
        </div>}

      {/* Empty State */}
      {!loading && !error && posts.length === 0 && <div className="rounded-2xl border border-border bg-card p-12 text-center">
          <BookOpen className="mx-auto size-12 text-muted-foreground" />
          <h3 className="mt-4 font-serif text-2xl font-semibold text-foreground">
            No matching guidance found
          </h3>
          <p className="mt-2 text-muted-foreground max-w-md mx-auto">
            Try adjusting your search terms or explore our primary health categories below.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <Button variant="outline" size="sm" onClick={() => {
          setLocalQuery("nutrition");
          onSearchSubmit("nutrition");
        }} className="rounded-full border-border bg-background">
              Nutrition
            </Button>
            <Button variant="outline" size="sm" onClick={() => {
          setLocalQuery("preventive care");
          onSearchSubmit("preventive care");
        }} className="rounded-full border-border bg-background">
              Preventive care
            </Button>
            <Button variant="outline" size="sm" onClick={() => {
          setLocalQuery("behavior");
          onSearchSubmit("behavior");
        }} className="rounded-full border-border bg-background">
              Behavior
            </Button>
            <Button variant="outline" size="sm" onClick={() => {
          setLocalQuery("breed guides");
          onSearchSubmit("breed guides");
        }} className="rounded-full border-border bg-background">
              Breed guides
            </Button>
          </div>
        </div>}

      {/* Results List */}
      {!loading && !error && posts.length > 0 && <div className="flex flex-col gap-6">
          {posts.map((post, i) => {
        const categoryName = post.categories && post.categories.length > 0 ? post.categories[0].name : "Veterinary Care";
        const authorName = post.author?.name || "PetsWhirl Clinical Board";
        const fallbackThumbnail = "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/vet_consultation_guidance.png";
        const imageSrc = post.featuredImage || fallbackThumbnail;
        return <Card key={post.id || i} data-index={i} className="overflow-hidden border border-border bg-card p-0 transition-[border-color,box-shadow] duration-200 hover:border-border/80 hover:shadow-md">
                <CardContent className="p-0">
                  <div className="flex flex-col md:flex-row items-stretch">
                    {/* Thumbnail Image */}
                    <div className="relative md:w-72 lg:w-80 shrink-0 bg-muted overflow-hidden">
                      <Link to={post.link || "#"} className="block h-48 md:h-full w-full">
                        <img src={imageSrc} alt={post.title || "Search article thumbnail"} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 hover:scale-105" />
                      </Link>
                    </div>

                    {/* Content Block */}
                    <div className="flex flex-1 flex-col justify-between p-6 md:p-7">
                      <div>
                        {/* Meta Category & Veterinary Badge */}
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                          <Badge variant="secondary" className="bg-muted text-foreground border-border rounded-full px-3 py-0.5 text-xs font-semibold">
                            {categoryName}
                          </Badge>
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground font-medium">
                            <UserCheck className="size-3.5 text-muted-foreground" />
                            Veterinary Reviewed
                          </span>
                        </div>

                        {/* Article Title */}
                        <h3 className="font-serif text-xl sm:text-2xl font-semibold leading-snug text-foreground">
                          <Link to={post.link || "#"} className="hover:text-foreground/80 transition-colors">
                            {post.title}
                          </Link>
                        </h3>

                        {/* Matched Excerpt */}
                        {post.excerpt && <p className="mt-3 text-sm leading-relaxed text-muted-foreground line-clamp-2">
                            {post.excerpt.replace(/<[^>]+>/g, "")}
                          </p>}
                      </div>

                      {/* Author Bylines and Read Link */}
                      <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-border/60 pt-4">
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="font-medium text-foreground">
                            By {authorName}
                          </span>
                          {post.date && <>
                              <span aria-hidden="true">•</span>
                              <span className="flex items-center gap-1">
                                <SafeHydrate><Calendar className="size-3 text-muted-foreground" /></SafeHydrate>
                                {new Date(post.date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric"
                        })}
                              </span>
                            </>}
                        </div>

                        <Button asChild variant="ghost" size="sm" className="gap-1.5 text-xs font-semibold text-foreground hover:bg-muted p-0 px-2.5 h-8">
                          <Link to={post.link || "#"}>
                            Read Full Guide
                            <ArrowRight className="size-3.5" />
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>;
      })}

          {/* Pagination Navigation */}
          {totalPages > 1 && <div className="mt-8 flex items-center justify-between border-t border-border pt-6">
              <Button variant="outline" size="sm" onClick={prevPage} disabled={currentPage <= 1 || isRefetching} className="gap-1 rounded-lg border-border bg-background">
                <ChevronLeft className="size-4" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page <strong className="text-foreground">{currentPage}</strong> of {totalPages}
              </span>
              <Button variant="outline" size="sm" onClick={nextPage} disabled={currentPage >= totalPages || isRefetching} className="gap-1 rounded-lg border-border bg-background">
                Next
                <ChevronRight className="size-4" />
              </Button>
            </div>}
        </div>}
    </div>;
}
export default function Results() {
  const [searchTerm, setSearchTerm] = useState("");
  const wp_query_params = {
    post_type: "post",
    per_page: 6,
    order: "desc",
    orderby: "date",
    ...(searchTerm ? {
      search: searchTerm
    } : {})
  };
  const wp_query = new WP_Query(wp_query_params);
  const handleSearchSubmit = (newTerm: string) => {
    setSearchTerm(newTerm);
  };
  return <section id="results-list" data-nav="light" className="bg-background py-12 md:py-16 lg:py-20 text-foreground">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {/* Editorial Section Header */}
        <div className="mb-10 text-center md:text-left">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            Clinical Knowledge Directory
          </p>
          <h1 className="mt-2 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground">
            Search pet health knowledge
          </h1>
          <p className="mt-3 text-base sm:text-lg text-muted-foreground max-w-2xl">
            Find veterinary-reviewed guides, breed profiles, and nutrition research.
          </p>
        </div>

        {/* Dynamic Provider Boundary */}
        <PostsProvider key={searchTerm} className="w-full" wp_query={wp_query}>
          <ResultsConsumer searchInput={searchTerm} onSearchSubmit={handleSearchSubmit} />
        </PostsProvider>
      </div>
    </section>;
}